import React, { useRef, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { API_URL } from '../config';
import useModalBackButton from '../hooks/useModalBackButton';
import ModalShell from './ModalShell';
import { ModalListSkeleton } from './Skeleton';
import { getDiscountRoleBlockedReason } from '../utils/discountAccess';
import { haptic } from '../utils/vibration';

const getDiscountBlockedReason = (discount, context = {}) => {
  if (discount.system_code === 'no_delivery') {
    if (context.returnDeliveryKnown === false) {
      return 'Скидка «Заказ без доставок» станет доступна только после того, как будет понятен способ возврата клиенту.';
    }
    const noDeliveries = !!context.selfDelivery && !context.hasPickupDelivery && !context.hasReturnDelivery;
    if (!noDeliveries) {
      return 'Скидка «Заказ без доставок» доступна только если не было доставки от клиента и доставки до клиента.';
    }
  }

  if (discount.system_code === 'wholesale_order') {
    const minItems = Number(discount.min_items_count || 1);
    const itemsCount = Number(context.itemsCount || 0);
    if (itemsCount < minItems) {
      return `Скидка «Оптовый заказ» доступна от ${minItems} предметов. Сейчас в заказе: ${itemsCount}.`;
    }
  }

  return null;
};

const DiscountSelectorModal = ({
  open,
  onClose,
  onSelect,
  onRemove,
  clientType = 'b2c',
  existingItems = [],
  orderContext = {},
  onBlocked,
  canManageSystemDiscounts = false,
  currentUserType = localStorage.getItem('user_type') || '',
  currentUserTypeLoaded = true
}) => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [confirmRemovalDiscount, setConfirmRemovalDiscount] = useState(null);
  const pendingIdsRef = useRef(new Set());
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open) return;
    const existing = new Set();
    existingItems.forEach(item => {
      if (item.isAdditional && item.additionalType === 'discount' && item.discountId) {
        existing.add(Number(item.discountId));
      }
    });
    setSelectedIds(existing);
  }, [open, existingItems]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/discount-templates?client_type=${clientType}&active_only=true`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setDiscounts(Array.isArray(data) ? data : []))
      .catch(() => setDiscounts([]))
      .finally(() => setLoading(false));
  }, [open, clientType]);

  const isAutoSystemDiscount = (discount) => !!(discount?.system_code && discount?.auto_apply_enabled);

  const toggleDiscount = async (discount) => {
    const discountId = Number(discount.id);
    if (pendingIdsRef.current.has(discountId)) return;

    const wasSelected = selectedIds.has(discountId);
    const autoSystemDiscount = isAutoSystemDiscount(discount);
    if (!currentUserTypeLoaded) {
      haptic('warning');
      onBlocked?.('Права пользователя ещё загружаются. Попробуйте через секунду.');
      return;
    }
    const roleBlockedReason = getDiscountRoleBlockedReason(discount, currentUserType);
    if (roleBlockedReason) {
      haptic('warning');
      onBlocked?.(roleBlockedReason);
      return;
    }
    if (autoSystemDiscount && !canManageSystemDiscounts) {
      haptic('warning');
      onBlocked?.('Эта скидка применяется автоматически. Изменить её может только диспетчер или администратор.');
      return;
    }
    if (!wasSelected) {
      const blockedReason = getDiscountBlockedReason(discount, orderContext);
      if (blockedReason) {
        haptic('warning');
        onBlocked?.(blockedReason);
        return;
      }
    } else if (autoSystemDiscount) {
      setConfirmRemovalDiscount(discount);
      haptic('warning');
      return;
    }
    haptic(wasSelected ? 'tap' : 'success');
    pendingIdsRef.current.add(discountId);
    setPendingIds(prev => {
      const next = new Set(prev);
      next.add(discountId);
      return next;
    });
    setSelectedIds(prev => {
      const next = new Set(prev);
      wasSelected ? next.delete(discountId) : next.add(discountId);
      return next;
    });

    try {
      if (wasSelected) {
        const result = await onRemove(discount.id);
        if (result === false) {
          throw new Error('Не удалось изменить скидку');
        }
      } else {
        const result = await onSelect(discount);
        if (result === false) {
          throw new Error('Не удалось изменить скидку');
        }
      }
    } catch (error) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        wasSelected ? next.add(discountId) : next.delete(discountId);
        return next;
      });
      throw error;
    } finally {
      pendingIdsRef.current.delete(discountId);
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(discountId);
        return next;
      });
    }
  };

  const confirmSystemDiscountRemoval = async () => {
    if (!confirmRemovalDiscount) return;
    const discount = confirmRemovalDiscount;
    const discountId = Number(discount.id);
    if (pendingIdsRef.current.has(discountId)) return;

    haptic('warning');
    pendingIdsRef.current.add(discountId);
    setPendingIds(prev => {
      const next = new Set(prev);
      next.add(discountId);
      return next;
    });

    try {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(discountId);
        return next;
      });
      const result = await onRemove(discount.id, { confirmSystemRemoval: true });
      if (result === false) {
        throw new Error('Не удалось снять скидку');
      }
      setConfirmRemovalDiscount(null);
    } catch (error) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.add(discountId);
        return next;
      });
      throw error;
    } finally {
      pendingIdsRef.current.delete(discountId);
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(discountId);
        return next;
      });
    }
  };

  const rolesLabel = { all: 'Все', dispatcher: 'Диспетчер+', admin: 'Только админ' };

  return (
    <ModalShell
      open={open}
      onClose={() => {
        haptic('close');
        onClose();
      }}
      variant="sheet"
      expandable={true}
      panelClassName="modal-bottom-sheet flex min-h-0 max-h-[75vh] flex-col overflow-hidden sm:max-w-lg"
      panelProps={{ style: { maxHeight: '75dvh', overflowY: 'hidden' } }}
    >
        {/* Заголовок */}
        <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Скидки заказа</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {clientType === 'b2b' ? 'Юр. лица' : 'Физ. лица'}
            </p>
          </div>
          <button onClick={() => { haptic('close'); onClose(); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <ModalListSkeleton count={4} />
          ) : discounts.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">Нет доступных скидок</p>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              {discounts.map(discount => {
                const discountId = Number(discount.id);
                const isSelected = selectedIds.has(discountId);
                const isPending = pendingIds.has(discountId);
                const autoSystemDiscount = isAutoSystemDiscount(discount);
                const roleBlockedReason = currentUserTypeLoaded
                  ? getDiscountRoleBlockedReason(discount, currentUserType)
                  : null;
                const availabilityBlockedReason = !isSelected ? getDiscountBlockedReason(discount, orderContext) : null;
                const blockedReason = roleBlockedReason || availabilityBlockedReason;
                const readonlySystemReason = autoSystemDiscount && !canManageSystemDiscounts
                  ? 'автоматически'
                  : null;
                const isBlocked = !currentUserTypeLoaded || !!blockedReason || !!readonlySystemReason;
                return (
                  <div
                    key={discount.id}
                    className={`flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
                      isSelected ? 'bg-orange-50 dark:bg-orange-900/20' : isBlocked ? 'bg-gray-50 opacity-75 dark:bg-gray-800/60' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <div className={`text-sm ${isBlocked ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>{discount.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        -{discount.discount_value}{discount.discount_type === 'percent' ? '%' : ' ₽'}
                        {discount.allowed_roles !== 'all' && ` • ${rolesLabel[discount.allowed_roles]}`}
                        {!currentUserTypeLoaded ? ' • проверка прав' : isBlocked && ' • недоступна'}
                        {readonlySystemReason && ' • авто'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleDiscount(discount)}
                      disabled={isPending || isBlocked}
                      className="toggle-switch shrink-0 disabled:opacity-60"
                      style={{
                        '--toggle-bg': isSelected ? 'rgb(249 115 22)' : isBlocked ? 'rgb(156 163 175)' : 'rgb(209 213 219)',
                        '--toggle-height': '22px',
                        '--toggle-width': '42px',
                        '--knob-x': isSelected ? '20px' : '2px'
                      }}
                    >
                      <span className="toggle-switch-knob" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {confirmRemovalDiscount && (
          <div className="shrink-0 border-t border-gray-200 bg-amber-50 p-4 dark:border-gray-700 dark:bg-amber-950/20">
            <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              Снять авто-скидку?
            </div>
            <div className="mt-1 text-xs leading-5 text-amber-800 dark:text-amber-200">
              Скидка «{confirmRemovalDiscount.name}» установлена автоматически. После подтверждения она не будет возвращаться автоматически, пока старшая роль не включит её снова.
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setConfirmRemovalDiscount(null);
                }}
                className="h-9 rounded-lg border-2 border-amber-300 px-3 text-sm font-medium text-amber-900 dark:border-amber-700 dark:text-amber-100"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={confirmSystemDiscountRemoval}
                className="h-9 rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white"
              >
                Снять скидку
              </button>
            </div>
          </div>
        )}
    </ModalShell>
  );
};

export default DiscountSelectorModal;
